import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { DropzoneInputProps, FileWithPath } from 'react-dropzone'
import { IoTrash } from 'react-icons/io5'
import { FaPlus, FaPlayCircle } from 'react-icons/fa'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'
import AddDonorButton from '@components/Request/AddDonorButton'
import ClinicInfoCards from '@components/InfoCards/ClinicInfoCards'
import PatientInfoCards from '@components/InfoCards/PatientInfoCards'
import ImageLightbox from '@components/ImageLightbox/ImageLightbox'
import Edit from '@resources/imgs/edit.svg'
import AuthContext from '@contexts/Auth'
import IDonor from '@interfaces/IDonor'
import * as $Order from '@services/Order'
import * as $Auth from '@services/Auth'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'
import moment from 'moment'

import { CheckoutDonorCard } from '@components/Cards/DonorCard'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import Mask from '@helpers/Mask'
import { FaCircleExclamation, FaCircleQuestion } from 'react-icons/fa6'

const TUTORIAL_VIDEO_ID = 'B3ajXRVYpTc'

interface PhotoMatchImage {
  id: string
  file: File
  preview: string
}

interface CheckoutStepProps {
  formData: any
  selectedTreatment: string
  photomatchImages: PhotoMatchImage[]
  dropzoneVisible: boolean
  handleCheckboxChange: (checked: boolean) => void
  handleRemoveImage: (id: string) => void
  handleDrop: (acceptedFiles: FileWithPath[]) => void
  getRootProps: any
  getInputProps: any
  onBack: () => void
  onFinishCheckout: () => void
  onEditClinic: () => void
  onEditDonors: () => void
  photomatchPatientObservation: string
  observation: string
  onPhotomatchPatientObservationChange: (value: string) => void
  onObservationChange: (value: string) => void
  isSubmitting: boolean
}

const CheckoutStep: React.FC<CheckoutStepProps> = ({
  selectedTreatment,
  photomatchImages,
  dropzoneVisible,
  handleCheckboxChange,
  handleRemoveImage,
  getRootProps,
  getInputProps,
  onBack,
  onFinishCheckout,
  onEditClinic,
  onEditDonors,
  photomatchPatientObservation,
  observation,
  onPhotomatchPatientObservationChange,
  onObservationChange,
  isSubmitting,
}) => {
  const { user, cart, onRemoveDonorFromCart, setUser } = useContext(AuthContext)

  const [donors, setDonors] = useState<IDonor[]>([])

  const navigate = useNavigate()

  const SweetAlert = useMemo(() => withReactContent(Swal), [])

  const fetchDonors = useCallback(() => {
    return $Order.donors().then(({ data: fetchedDonors }) => {
      setDonors(fetchedDonors)
    }).catch((error) => {
      SweetAlert.fire('Erro', error.message || 'Erro ao buscar doadores do carrinho', 'error')
      navigate('/')
    })
  }, [navigate, SweetAlert])

  const fetchUser = useCallback(() => {
    return $Auth.me().then(({ data: fetchedUser }) => {
      setUser(fetchedUser)
    }).catch((error) => {
      SweetAlert.fire('Erro', error.message || 'Erro ao atualizar seus dados', 'error')
    })
  }, [SweetAlert, setUser])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setDonors((items) => {
        const oldIndex = items.findIndex((item) => item.internal_id === active.id)
        const newIndex = items.findIndex((item) => item.internal_id === over.id)

        const newOrder = arrayMove(items, oldIndex, newIndex)

        // Call API to update order
        const donorIds = newOrder.map((donor) => String(donor.internal_id))
        $Order.reorderDonors(donorIds).catch((error) => {
          SweetAlert.fire('Erro', error.message || 'Erro ao reordenar doadores', 'error')
          // Revert on error
          setDonors(items)
        })

        return newOrder
      })
    }
  }

  useEffect(() => {
    fetchDonors()
    fetchUser()
  }, [fetchDonors, fetchUser])

  useEffect(() => {
    const handleFocus = () => {
      fetchDonors()
      fetchUser()
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [fetchDonors, fetchUser])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (observation.trim() || photomatchPatientObservation.trim()) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [observation, photomatchPatientObservation])

  const handleRemoveFromCart = (id: string) => {
    onRemoveDonorFromCart(id).then(() => {
      setDonors((prevDonors) => prevDonors.filter((donor) => donor.internal_id !== id))
    })
  }

  const userPhone = useMemo(() => {
    if (!user) return ''

    return Mask.cellphoneView(user.whatsapp || user.cellphone || user.telephone || '')
  }, [user])

  const clinicAddress = useMemo(() => {
    if (!cart.clinic?.main_address) return null

    const mainAddress = cart.clinic.main_address
    const { city, state } = mainAddress

    return {
      city,
      state
    }
  }, [cart.clinic])

  const clinicShippingData = useMemo(() => {
    if (!cart?.shipping_type) return null

    const shippingPayment = cart.shipping_type
    const transportValue = cart?.transport_value ?? 0

    let res: {
      title: string
      description: string
      price: number | string
    }

    switch (shippingPayment) {
    case 'pro-seed':
      res = {
        title: 'Pagamento é feito para o Pro-Seed.',
        description: 'O custo de transporte será adicionado ao valor da amostra de sêmen.',
        price: transportValue,
      }
      break
    case 'shipping-company':
      res = {
        title: 'O pagamento do transporte é feito diretamente para a transportadora.',
        description: 'O custo do transporte é apresentado no pedido, mas não está incluso no valor da amostra de sêmen.',
        price: transportValue,
      }
      break
    case 'clinic':
      res = {
        title: 'Pagamento é feito através da clínica.',
        description: 'Toda a gestão do processo logístico é de responsabilidade da clínica.',
        price: 0,
      }
      break
    }

    return res
  }, [cart?.shipping_type, cart?.transport_value])

  const formattedTreatmentDate = useMemo(() => {
    if (!cart.treatment_date) return '-'

    return moment(cart.treatment_date).format('DD/MM/YYYY')
  }, [cart.treatment_date])

  const hasAtLeastThreeDonors = useMemo(() => donors.length >= 3, [donors])

  const hasAtLeastThreeWithSameBank = useMemo(() => {
    const spermBankCounts: Record<string, number> = {}
    donors.forEach(donor => {
      const id = donor.sperm_bank_id
      if (id && String(id).trim()) {
        const bankId = String(id)
        spermBankCounts[bankId] = (spermBankCounts[bankId] || 0) + 1
      }
    })
    return Object.values(spermBankCounts).some(count => count >= 3)
  }, [donors])

  const isPhotoMatchAvailable = useMemo(() => {
    return hasAtLeastThreeDonors && hasAtLeastThreeWithSameBank
  }, [hasAtLeastThreeDonors, hasAtLeastThreeWithSameBank])

  const photoMatchData = useMemo(() => {
    if (!isPhotoMatchAvailable) return { donors: [], bankIds: [] }

    const spermBankCounts: Record<string, number> = {}
    donors.forEach(d => {
      const id = d.sperm_bank_id
      if (id && String(id).trim()) {
        const bankId = String(id)
        spermBankCounts[bankId] = (spermBankCounts[bankId] || 0) + 1
      }
    })
    const eligibleBankIds = Object.keys(spermBankCounts).filter(id => spermBankCounts[id] >= 3)
    const eligibleDonors = donors.filter(d => d.sperm_bank_id && eligibleBankIds.includes(String(d.sperm_bank_id)))
    return { donors: eligibleDonors, bankIds: eligibleBankIds }
  }, [donors, isPhotoMatchAvailable])

  const photoMatchEligibleDonors = photoMatchData.donors
  const eligibleBankIds = photoMatchData.bankIds

  const hasDonorWithoutEligibleTreatment = useMemo(() => {
    return donors.some((donor) => {
      const availableTreatments = donor?.available_treatment_names ?? []
      return selectedTreatment && !availableTreatments.includes(selectedTreatment === 'Inseminação' ? 'IIU' : selectedTreatment)
    })
  }, [donors, selectedTreatment])

  const handlePhotoMatchCheckboxChange = (checked: boolean) => {
    if (!isPhotoMatchAvailable) return
    handleCheckboxChange(checked)
  }

  const [lightboxImage, setLightboxImage] = useState<string | null>(null)

  const [showVideoModal, setShowVideoModal] = useState(false)
  const [showVideo2Modal, setShowVideo2Modal] = useState(false)
  const [showVideo3Modal, setShowVideo3Modal] = useState(false)
  const [showVideo4Modal, setShowVideo4Modal] = useState(false)
  const [showVideo5Modal, setShowVideo5Modal] = useState(false)
  const [showVideo6Modal, setShowVideo6Modal] = useState(false)
  const [showTutorialVideoModal, setShowTutorialVideoModal] = useState(false)

  const handleHelpClick = () => setShowVideoModal(true)
  const handleCloseModal = () => setShowVideoModal(false)

  const handleHelp2Click = () => setShowVideo2Modal(true)
  const handleClose2Modal = () => setShowVideo2Modal(false)

  const handleHelp3Click = () => setShowVideo3Modal(true)
  const handleClose3Modal = () => setShowVideo3Modal(false)

  const handleHelp4Click = () => setShowVideo4Modal(true)
  const handleClose4Modal = () => setShowVideo4Modal(false)

  const handleHelp5Click = () => setShowVideo5Modal(true)
  const handleClose5Modal = () => setShowVideo5Modal(false)

  const handleHelp6Click = () => setShowVideo6Modal(true)
  const handleClose6Modal = () => setShowVideo6Modal(false)

  const handleTutorialClick = () => setShowTutorialVideoModal(true)
  const handleCloseTutorialModal = () => setShowTutorialVideoModal(false)

  const handleOpenLightbox = (imageUrl: string) => setLightboxImage(imageUrl)

  const handleCloseLightbox = () => setLightboxImage(null)

  return (
    <div className="checkout-step request-summer-page">
      <div className="row">
        <div className="col-md-12 line title">
          <h5>Revise sua solicitação</h5>
          <p>Confira se todos os dados estão corretos para que possamos seguir com a solicitação.</p>
          <button
            type="button"
            className="btn btn-link p-0 mt-1 d-none align-items-center gap-2"
            onClick={handleTutorialClick}
          >
            <FaPlayCircle /> Assista ao passo a passo para solicitar amostra
          </button>
        </div>
        <div className="col-md-12">
          <div className="check-sample">
            {/* Clinic Information */}
            <div className="check check-clinic">
              <div className="title pb-3 d-flex align-items-center mt-4 justify-content-between">
                <h6>
                  Revise os seus dados:
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <FaCircleQuestion style={{ color: '#FA5A84', marginLeft: '5px', cursor: 'pointer', fontSize: '12px' }} onClick={handleHelpClick} />
                  </span>
                </h6>
                <button type="button" className="btn px-0 btn-edit d-flex gap-5" onClick={onEditClinic}>
                  <img src={Edit} alt="" />
                  <span className="ml-2">Editar</span>
                </button>
              </div>

              <div className="summary">
                <ClinicInfoCards
                  clinicName={cart.clinic?.trading_name || ''}
                  clinicCity={clinicAddress?.city || ''}
                  clinicState={clinicAddress?.state || ''}
                  doctorName={cart.doctor_name || ''}
                  doctorCrm={cart.doctor_crm || ''}
                  treatmentType={cart.treatment_type || ''}
                  treatmentDate={formattedTreatmentDate}
                />
              </div>
            </div>

            {/* Section Divider */}
            <div className="section-divider">
              <div className="section-divider__line" />
              <span className="section-divider__text">Informações da Paciente</span>
              <div className="section-divider__line" />
            </div>

            {/* Patient Information */}
            <div className="check check-patient">
              <div className="title mt-2 pb-3 d-flex align-items-center justify-content-between">
                <h6>
                  Revise os dados do paciente:
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <FaCircleQuestion style={{ color: '#FA5A84', marginLeft: '5px', cursor: 'pointer', fontSize: '12px' }} onClick={handleHelp2Click} />
                  </span>
                </h6>
                <Link className="btn-edit d-flex gap-5 ml-2" to="/perfil" target="_blank" rel="noopener noreferrer">
                  <img src={Edit} alt="" />
                  <span className="ml-2">Editar</span>
                </Link>
              </div>

              <div className="summary">
                <PatientInfoCards user={user} userPhone={userPhone} />
              </div>
            </div>

            {/* Section Divider */}
            <div className="section-divider">
              <div className="section-divider__line" />
              <span className="section-divider__text">Doadores Selecionados</span>
              <div className="section-divider__line" />
            </div>
          </div>

          <div className="line check check-donors box-card">
            <div className="title d-flex align-items-center pb-1 mt-4 justify-content-between">
              <h6>
                Revise os doadores selecionados:
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <FaCircleQuestion style={{ color: '#FA5A84', marginLeft: '5px', cursor: 'pointer', fontSize: '12px' }} onClick={handleHelp3Click} />
                </span>
              </h6>
              <button type="button" className="btn px-0 btn-edit d-flex gap-5" onClick={onEditDonors}>
                <img src={Edit} alt="" />
                <span className="ml-2">Editar</span>
              </button>
            </div>
            <p>A ordem de prioridade é crucial, pois consideramos a sua primeira opção e ordem de preferência para fazer a reserva.</p>
            <p>Nos esforçamos para reservar sempre seu doador prioritário, mas isso nem sempre é possível. Além da questão da disponibilidade de estoque de amostras do doador escolhido, também verificamos o limite de nascimentos por doador para a sua região.</p>
            <div className="row align-items-center">
              <div className="col-12">
                <div className="card-index mt-2">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={donors.map((d) => String(d.internal_id))}
                      strategy={verticalListSortingStrategy}
                    >
                      {donors.map((donor, i) => (
                        <CheckoutDonorCard
                          key={donor.internal_id}
                          donor={donor}
                          position={i+1}
                          selectedTreatment={selectedTreatment}
                          onRemove={handleRemoveFromCart}
                          isDraggable={true}
                          isLast={i === donors.length - 1}
                          showPhotoMatchColumn={isPhotoMatchAvailable}
                          isPhotoMatchEligible={donor.sperm_bank_id ? eligibleBankIds.includes(String(donor.sperm_bank_id)) : false}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
            </div>
            {!isPhotoMatchAvailable && (
              <div className="mt-3">
                <AddDonorButton currentCount={donors.length} maxCount={6} showCounter={true} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="line services mt-4">
        <div className="title pb-3">
          <h6>
            Conte com serviços adicionais:
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <FaCircleQuestion style={{ color: '#FA5A84', marginLeft: '5px', cursor: 'pointer', fontSize: '12px' }} onClick={handleHelp4Click} />
            </span>
          </h6>
        </div>
        <div className="row">
          <div className="col-12">
            <div className={`service ${isPhotoMatchAvailable ? '' : 'disabled'}`}>
              <div className="service-box">
                <div className="d-flex align-items-start gap-10 ml-2">
                  <input
                    className="checkbox"
                    type="checkbox"
                    checked={dropzoneVisible && isPhotoMatchAvailable}
                    onChange={(event) => handlePhotoMatchCheckboxChange(event.target.checked)}
                    disabled={!isPhotoMatchAvailable}
                  />
                  <div className="text w-100">
                    <h6>Photomatching</h6>
                    <p className="mb-0">Através deste serviço gratuito comparamos as suas opções de doadores pré-selecionados com as fotos que você nos enviar como referência e informamos qual doador mais se aproxima das características desejadas.</p>
                    {isPhotoMatchAvailable && photoMatchEligibleDonors.length > 0 && (
                      <p className="mt-2">
                        Doadores aptos para o Photomatching: <strong>{photoMatchEligibleDonors.map(d => d.internal_id).join(', ')}</strong>
                      </p>
                    )}
                    {(!hasAtLeastThreeDonors || !hasAtLeastThreeWithSameBank) && (
                      <p className="text-danger">
                        <strong>Serviço indisponível:</strong> O Photomatching requer pelo menos 3 doadores <b>do mesmo banco selecionados</b>. Adicione mais doadores acima para habilita-lo.
                      </p>
                    )}
                  </div>
                </div>
                <div className={`zone w-100 mt-3 ${dropzoneVisible && isPhotoMatchAvailable ? 'd-block' : 'd-none'}`}>
                  <div className="dropzone-area">
                    <div className="title pb-3">
                      <h6>Enviar apenas fotos de uma pessoa como referência.</h6>
                      <p className="text-muted mb-0" style={{ fontSize: '14px', fontWeight: 700 }}>
                        Mínimo de 2 fotos necessárias. {photomatchImages.length > 0 && (
                          <span className={photomatchImages.length < 2 ? 'text-warning' : 'text-success'}>
                            ({photomatchImages.length} {photomatchImages.length === 1 ? 'foto enviada' : 'fotos enviadas'})
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-5">
                      {photomatchImages.map((image, index) => (
                        <div key={image.id} className="single-image dinamica">
                          <img
                            src={image.preview}
                            alt="Imagem photomatching"
                            className={`image ${index === photomatchImages.length - 1 ? 'last' : ''}`}
                            onClick={() => handleOpenLightbox(image.preview)}
                            style={{ cursor: 'pointer' }}
                          />
                          <button
                            className="remove-button icons trash-icon"
                            onClick={() => handleRemoveImage(image.id)}
                          >
                            <IoTrash />
                          </button>
                        </div>
                      ))}
                      <div {...getRootProps()} className="dropzone d-flex flex-column align-items-center justify-content-center">
                        <input {...getInputProps() as DropzoneInputProps} />
                        <div className="dropzone-icon">
                          <FaPlus />
                        </div>
                        <span className="dropzone-title">Adicionar fotos</span>
                        <span className="dropzone-subtitle">Arraste ou clique aqui</span>
                        <span className="dropzone-subtitle">Tipo de arquivos png, jpg, jpeg com tamanho máximo de 10MB</span>
                      </div>
                    </div>
                    <div className="w-100">
                      <div className="text-area mt-4">
                        <textarea
                          className="form-control no-resize"
                          placeholder="Escreva aqui detalhes e observações..."
                          rows={4}
                          value={photomatchPatientObservation}
                          onChange={(event) => onPhotomatchPatientObservationChange(event.target.value)}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="line services mt-4">
        <div className="title pb-3">
          <h6>
            Aconselhamento Genético Patrocinado:
          </h6>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="service">
              <div className="text">
                <p>Para que você se sinta segura e tome uma decisão bem-informada, o Pro-Seed oferece uma consulta com geneticista especializada, que poderá explicar o(s) laudo(s) e esclarecer todas as suas dúvidas! Finalize sua pré-reserva antes de agendar essa conveniência.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="line transport">
        <div className="title pb-3 mt-4">
          <h6>
            Detalhes da Compra
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <FaCircleQuestion style={{ color: '#FA5A84', marginLeft: '5px', cursor: 'pointer', fontSize: '12px' }} onClick={handleHelp5Click} />
            </span>
          </h6>
          <p>Os valores final da amostra de sêmen e custos de transporte serão confirmados com a reserva do doador.</p>
        </div>
        <div className="row">
          <div className="col-12">
            {donors.length === 1 ? (
              <div className="transport-payment service card-index">
                {donors.map((donor, i) => (
                  <CheckoutDonorCard
                    key={donor.internal_id}
                    donor={donor}
                    position={i+1}
                    selectedTreatment={selectedTreatment}
                    isLast={i === donors.length - 1}
                  />
                ))}
              </div>
            ) : (
              <div className="alert alert-warning transport-payment-alert d-flex align-items-center gap-10" role="alert">
                <FaCircleExclamation style={{ fontSize: '24px', color: '#4d61aa', flexShrink: 0 }} />
                <p className="mb-0">O valor de amostra de sêmen será informado após a confirmação do doador reservado.</p>
              </div>
            )}
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <div className="transport-payment service">
              <div className="text">
                <h6>Prazo de entrega de até {cart.clinic?.delivery_time} horas - o mais rápido do mercado!</h6>
                <h6>{clinicShippingData?.title || ''}</h6>
                <div className="text-price d-flex justify-content-between">
                  <p>{clinicShippingData?.description || ''}</p>
                  {clinicShippingData?.price && Number(clinicShippingData?.price) > 0 ? (
                    <div className="price">
                      <b>{Number(clinicShippingData.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</b>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="comment">
        <div className="title pb-3 mt-4">
          <h6>
            Observação para a Equipe Pro-Seed
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <FaCircleQuestion style={{ color: '#FA5A84', marginLeft: '5px', cursor: 'pointer', fontSize: '12px' }} onClick={handleHelp6Click} />
            </span>
          </h6>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="text-area">
              <textarea
                className="form-control no-resize"
                placeholder="Deixe sua mensagem ou observações - vamos considerá-la na tratativa do seu pedido!"
                rows={3}
                value={observation}
                onChange={(event) => onObservationChange(event.target.value)}
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <div className="request-footer">
        <div className="row">
          <div className="col-12">
            {dropzoneVisible && photomatchImages.length < 2 && (
              <div
                className="alert d-flex align-items-center mb-3"
                role="alert"
                style={{
                  backgroundColor: '#FFF3CD',
                  border: '1px solid #FFC107',
                  borderRadius: '8px',
                  padding: '12px 16px'
                }}
              >
                <FaCircleExclamation style={{ marginRight: '10px', fontSize: '18px', color: '#F59E0B', flexShrink: 0 }} />
                <span style={{ fontSize: '14px', color: '#856404' }}>
                  Para prosseguir com o Photomatching, é necessário enviar no mínimo 2 fotos.
                </span>
              </div>
            )}
          </div>
          <div className="col-12 d-flex align-items-center justify-content-between">
            <button className="return-btn btn btn-primary" onClick={onBack}>
              <MdKeyboardArrowLeft />
              Voltar
            </button>
            <button
              className="next-btn btn btn-primary"
              onClick={onFinishCheckout}
              disabled={isSubmitting || hasDonorWithoutEligibleTreatment || (dropzoneVisible && photomatchImages.length < 2)}
            >
              {isSubmitting ? 'Enviando...' : 'Realizar Solicitação'}
              <MdKeyboardArrowRight />
            </button>
          </div>
        </div>
      </div>
      {showVideoModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleCloseModal}>
          <div style={{ position: 'relative', width: 'min(1280px, 90vw)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} src="https://www.youtube.com/embed/B3ajXRVYpTc" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            </div>
            <button style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }} onClick={handleCloseModal}>×</button>
          </div>
        </div>
      )}
      {showVideo2Modal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleClose2Modal}>
          <div style={{ position: 'relative', width: 'min(1280px, 90vw)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} src="https://www.youtube.com/embed/tQ2aXAhN9Fg" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            </div>
            <button style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }} onClick={handleClose2Modal}>×</button>
          </div>
        </div>
      )}
      {showVideo3Modal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleClose3Modal}>
          <div style={{ position: 'relative', width: 'min(1280px, 90vw)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} src="https://www.youtube.com/embed/hlrnQH0qyKc" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            </div>
            <button style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }} onClick={handleClose3Modal}>×</button>
          </div>
        </div>
      )}
      {showVideo4Modal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleClose4Modal}>
          <div style={{ position: 'relative', width: 'min(1280px, 90vw)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} src="https://www.youtube.com/embed/5xzSRv-GM80" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            </div>
            <button style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }} onClick={handleClose4Modal}>×</button>
          </div>
        </div>
      )}
      {showVideo5Modal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleClose5Modal}>
          <div style={{ position: 'relative', width: 'min(1280px, 90vw)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} src="https://www.youtube.com/embed/1axfTMgTgDs" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            </div>
            <button style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }} onClick={handleClose5Modal}>×</button>
          </div>
        </div>
      )}
      {showVideo6Modal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleClose6Modal}>
          <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }} onClick={(e) => e.stopPropagation()}>
            <iframe width="1280" height="720" src="https://www.youtube.com/embed/D4VxoNOixMY" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            <button style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }} onClick={handleClose6Modal}>×</button>
          </div>
        </div>
      )}
      {showTutorialVideoModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleCloseTutorialModal}>
          <div style={{ position: 'relative', width: 'min(1280px, 90vw)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} src={`https://www.youtube.com/embed/${TUTORIAL_VIDEO_ID}`} title="Passo a passo para solicitar amostra" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            </div>
            <button style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }} onClick={handleCloseTutorialModal}>×</button>
          </div>
        </div>
      )}
      <ImageLightbox
        isOpen={!!lightboxImage}
        imageUrl={lightboxImage || ''}
        onClose={handleCloseLightbox}
      />
      {/* Summary modal removed: redirection handled in parent after successful checkout */}
    </div>
  )
}

export default CheckoutStep